import { createRouter, createWebHistory } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import LoginView     from '@/views/LoginView.vue'
import DashboardView from '@/views/DashboardView.vue'
import TasksView     from '@/views/TasksView.vue'
import CalendarView  from '@/views/CalendarView.vue'
import ProjectsView  from '@/views/ProjectsView.vue'
import TeamView      from '@/views/TeamView.vue'
import TeamDirectoryView from '@/views/TeamDirectoryView.vue'
import RequestsView  from '@/views/RequestsView.vue'
import ReportsView   from '@/views/ReportsView.vue'
import ProfileView   from '@/views/ProfileView.vue'
// Client portal (external clients only)
import ClientDashboard     from '@/views/client/ClientDashboard.vue'
import ClientProjects      from '@/views/client/ClientProjects.vue'
import ClientProjectDetail from '@/views/client/ClientProjectDetail.vue'
import ClientRequests      from '@/views/client/ClientRequests.vue'
import ClientReports       from '@/views/client/ClientReports.vue'

const routes = [
  { path: '/login',    name: 'login',     component: LoginView,     meta: { public: true } },

  // Internal (dev/manager) app
  { path: '/',         name: 'dashboard', component: DashboardView,    meta: { internalOnly: true } },
  { path: '/tasks',    name: 'tasks',     component: TasksView,        meta: { internalOnly: true } },
  { path: '/calendar', name: 'calendar',  component: CalendarView,     meta: { internalOnly: true } },
  { path: '/projects', name: 'projects',  component: ProjectsView,     meta: { internalOnly: true } },
  { path: '/team',     name: 'team',      component: TeamDirectoryView, meta: { internalOnly: true } },
  { path: '/clients',  name: 'clients',   component: TeamView,         meta: { internalOnly: true } },
  { path: '/reports',  name: 'reports',   component: ReportsView,      meta: { internalOnly: true } },
  { path: '/requests', name: 'requests',  component: RequestsView,     meta: { internalOnly: true } },
  { path: '/profile',  name: 'profile',   component: ProfileView,      meta: { internalOnly: true } },

  // Client portal
  { path: '/portal',           name: 'c-dashboard', component: ClientDashboard,     meta: { clientOnly: true } },
  { path: '/portal/projects',  name: 'c-projects',  component: ClientProjects,      meta: { clientOnly: true } },
  { path: '/portal/projects/:id', name: 'c-project', component: ClientProjectDetail, meta: { clientOnly: true } },
  { path: '/portal/requests',  name: 'c-requests',  component: ClientRequests,      meta: { clientOnly: true } },
  { path: '/portal/reports',   name: 'c-reports',   component: ClientReports,       meta: { clientOnly: true } },

  { path: '/:catchAll(.*)', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach((to) => {
  const token = localStorage.getItem('todo_token')
  const user  = JSON.parse(localStorage.getItem('todo_user') || 'null')
  const isExternal = user?.user_type === 'external'

  if (!to.meta.public && !token) {
    return { name: 'login' }
  }
  if (to.name === 'login' && token) {
    return isExternal ? { name: 'c-dashboard' } : { name: 'dashboard' }
  }
  // Keep the two apps separate: clients live under /portal, internal users don't.
  if (isExternal && !to.meta.public && !to.meta.clientOnly) {
    return { name: 'c-dashboard' }
  }
  if (!isExternal && token && to.meta.clientOnly) {
    return { name: 'dashboard' }
  }
  if (to.meta.managerOnly && user?.role !== 'manager') {
    return { name: 'dashboard' }
  }
})

// ── Document titles ──
// Each route resolves to a page name; the tab/title becomes "Page — DevTask" so
// browser tabs, history and bookmarks are meaningful. Titles are localized and
// re-resolve on every client-side navigation. (The <html lang>/dir attribute is
// kept correct by applyLocale() in useI18n on boot and on every locale toggle.)
const APP_NAME = 'PICO DEV'
const TITLE_KEYS = {
  login:        () => 'login.title',
  dashboard:    () => 'nav.dashboard',
  tasks:        (to) => (to.query.mine ? 'nav.myTasks' : 'nav.tasks'),
  calendar:     () => 'nav.calendar',
  projects:     () => 'nav.projects',
  team:         () => 'nav.team',
  clients:      () => 'nav.clients',
  reports:      () => 'nav.reports',
  requests:     () => 'nav.requests',
  profile:      () => 'profile.title',
  'c-dashboard':() => 'portal.navOverview',
  'c-projects': () => 'portal.navProjects',
  'c-project':  () => 'portal.navProjects',
  'c-requests': () => 'portal.navRequests',
  'c-reports':  () => 'portal.navReports',
}

router.afterEach((to) => {
  const { t } = useI18n()
  const resolver = TITLE_KEYS[to.name]
  const name = resolver ? t(resolver(to)) : ''
  document.title = name ? `${name} — ${APP_NAME}` : APP_NAME
})

export default router
